/** プレイス詳細表示コンポーネント */
<template>
  <ifield-b-modal
    id="the-place-detail"
    ref="the-place-detail-modal"
    v-bind:title="$t('thePlaceDetail.place')"
    size="xl"
    centered
    no-body-border
    body-class="place-detail-dialog-body"
    static
    modal-class="detail-dialog-static"
    v-on:show="$_onShowModal"
    v-on:hide="$_onHideModal"
  >
    <loading-mask v-if="isContentCreated" v-bind:isLoading="isLoading">
      <div class="content">
        <div class="header" v-bind:class="{ 'no-kana': !placeNameKana }">
          <div class="place-name">
            <h4 class="name">{{ placeName }}</h4>
            <p v-if="placeNameKana" class="kana">{{ placeNameKana }}</p>
          </div>
          <div class="action-text">
            <!-- ユーザー検索はGPS権限の無いReportサービスでは利用不可 -->
            <p
              v-if="isGpsAvailable"
              class="clickable"
              v-on:click="$_onSearchUserClick"
            >
              {{ $t('thePlaceDetail.action.findUsersNearby') }}
            </p>
            <p
              v-if="isReportAvailable"
              v-bind:class="reportCount ? 'clickable' : 'normal'"
              v-on:click="$_onSearchReportClick"
            >
              {{
                `${$t('thePlaceDetail.action.searchReport')}(${reportCount})`
              }}
            </p>
            <p
              v-if="canSetTask"
              class="clickable"
              v-on:click="$_showScheduleEditor"
            >
              {{ $t('thePlaceDetail.action.sendTask') }}
            </p>
          </div>
        </div>
        <div
          class="panel-container"
          v-bind:class="{
            'first-view': viewIndex === 0,
            'last-view': viewIndex === viewCount - 1,
          }"
          v-bind:style="{ '--index': viewIndex }"
        >
          <div class="panels">
            <component
              v-for="(panel, index) in panels"
              v-bind:key="index"
              v-bind:is="panel.component"
              v-bind="panel.props"
              v-on="panel.listeners"
            />
          </div>
          <template v-if="hasMultiViews">
            <!-- 前/後ボタン -->
            <b-button
              v-if="viewIndex > 0"
              variant="secondary"
              class="move-button back"
              v-on:click="$_onBackClick"
            >
              <icon-arrow-down class="arrow" />
            </b-button>
            <b-button
              v-if="viewIndex < viewCount - 1"
              variant="secondary"
              class="move-button next"
              v-on:click="$_onNextClick"
            >
              <icon-arrow-down class="arrow" />
            </b-button>
          </template>
        </div>
        <div class="view-indicator" v-if="hasMultiViews">
          <div
            v-for="item in viewIndicators"
            v-bind:key="item.index"
            class="indicator-item"
            v-bind:class="{ active: item.active }"
            v-on:click="viewIndex = item.index"
          />
        </div>
      </div>
    </loading-mask>
    <place-editor
      v-if="isContentCreated"
      ref="place-editor"
      v-bind:idPrefix="'edit'"
      v-on:place-editend="editEnd($event)"
    />
    <!-- スケジュールエディタ -->
    <schedule-editor
      v-if="isContentCreated && isScheduleAvailable"
      ref="schedule-editor"
      v-bind:domainName="$t('thePlaceDetail.task')"
      sendMessage
    />

    <!-- modal footer -->
    <template v-slot:modal-footer="{ cancel }">
      <!-- 編集ボタン -->
      <b-button
        size="sm"
        variant="success"
        v-t="'thePlaceDetail.action.edit'"
        v-on:click="editStart"
      />
      <!-- 閉じるボタン -->
      <b-button
        size="sm"
        variant="secondary"
        v-t="'thePlaceDetail.action.close'"
        v-on:click="cancel"
      />
    </template>
  </ifield-b-modal>
</template>

<script>
import PlaceEditor from '@/views/ThePlaceEditor.vue'
import ScheduleEditor from '@/views/TheScheduleEditor.vue'
import { RestApiClient } from 'ifield-core-components'
import PanelPlaceDetailInfo from '@/components/place/AppPanelPlaceDetailInfo.vue'
import PanelPlaceDetailMap from '@/components/place/AppPanelPlaceDetailMap.vue'
import PanelPlaceDetailSensor from '@/components/place/AppPanelPlaceDetailSensor.vue'
import IconArrowDown from '@/assets/ifield/icon_arrow_down.svg?component'
import { FUNCTION_TYPE } from '@/constants.js'
import { toLocalTz, toLocalTzAttribute } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

export default {
  name: 'ThePlaceDetail',
  components: {
    'icon-arrow-down': IconArrowDown,
    'place-editor': PlaceEditor,
    'schedule-editor': ScheduleEditor,
  },
  props: {},
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      /**
       * 表示対象のプレイスID
       */
      placeId: null,
      /**
       * プレイス詳細データ
       *
       * API(/ifield/users/api/place/:id)のレスポンス
       * @type {Object}
       */
      data: null,
      /**
       * プレイスに対する報告書件数
       * @type {Number}
       */
      reportCount: 0,
      /**
       * プレイスデータを更新した場合はtrue
       * @type {boolean}
       */
      isUpdated: false,
      /**
       * ロード中の場合はtrue
       * @type {Boolean}
       */
      isLoading: false,
      /**
       * ビューの表示index
       * @type {Number}
       */
      viewIndex: 0,
      /**
       * ダイアログの中身を生成済みの場合はtrue
       *
       * 地図を作り直さないため、一度開いたら閉じても保持する
       * @type {Boolean}
       */
      isContentCreated: false,
      /**
       * ダイアログを表示中の場合はtrue
       *
       * 非表示の間は地図のリアクティブな描画を止める
       * @type {Boolean}
       */
      isModalShown: false,
    }
  },
  computed: {
    /**
     * ログインユーザーが管理者の場合はtrue
     * @type {Boolean}
     */
    isManager: function () {
      return this.$store.state.login.isManager
    },
    /**
     * 定期通知機能が利用可能か？
     * @type {Boolean}
     */
    isGpsAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.GPS)
    },
    /**
     * 報告書機能が利用可能か？
     * @type {Boolean}
     */
    isReportAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.REPORT)
    },
    /**
     * スケジュール機能が利用可能か？
     * @type {Boolean}
     */
    isScheduleAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.SCHEDULE)
    },
    /**
     * 屋内機能が利用可能か？
     * @type {Boolean}
     */
    isIndoorAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.INDOOR)
    },
    /**
     * センサーが利用可能か？
     * @type {Boolean}
     */
    isSensorAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.SENSOR)
    },
    /**
     * プレイス名
     * @type {String}
     */
    placeName: function () {
      return (this.data && this.data.place_name) || ''
    },
    /**
     * プレイス名(かな)
     * @type {String}
     */
    placeNameKana: function () {
      return (this.data && this.data.place_name_kana) || ''
    },
    /**
     * 報告書検索ボタンが有効な場合はture
     * @type {Boolean}
     */
    canSearchReport: function () {
      return this.reportCount > 0
    },
    /**
     * 報告書検索ボタンのvariant
     * @type {String}
     */
    searchReportButtonVariant: function () {
      return this.canSearchReport ? 'success' : 'secondary'
    },
    /**
     * 作業指示可能な場合はtrue
     * @type {Boolean}
     */
    canSetTask: function () {
      return this.isManager && this.isScheduleAvailable
    },
    /**
     * パネル定義
     * @type {Array.<Object>}
     */
    panels: function () {
      const panel = {
        info: {
          component: PanelPlaceDetailInfo,
          props: {
            place: this.data,
          },
          listeners: {
            'position-fix-click': this.$_fixPosition,
          },
        },
        map: {
          component: PanelPlaceDetailMap,
          props: {
            place: this.data,
            reactiveRender: this.isModalShown,
          },
        },
        sensor: {
          component: PanelPlaceDetailSensor,
          props: {
            place: this.data,
          },
        },
      }
      return this.isSensorAvailable
        ? [panel.info, panel.map, panel.sensor, panel.info]
        : [panel.info, panel.map]
    },
    /**
     * パネル表示ビューの枚数
     * @type {Number}
     */
    viewCount: function () {
      return this.panels.length - 1
    },
    /**
     * 複数のパネルビューがあるか？
     * @type {boolean}
     */
    hasMultiViews: function () {
      return this.viewCount >= 2
    },
    /**
     * パネルビューのインジケーター用データ
     * @type {Array.<Object>}
     */
    viewIndicators: function () {
      return [...Array(this.viewCount)].map((_, index) => ({
        index,
        active: index === this.viewIndex,
      }))
    },
  },
  mounted: function () {
    this.$log.info('placeDetail mounted')
  },
  methods: {
    /**
     * モーダルダイアログを表示する
     * @param {Object} recordId 表示対象のプレイスID
     */
    showModal: async function (recordId) {
      // データ初期化
      this.data = null
      this.placeId = recordId
      this.isUpdated = false
      this.$refs['the-place-detail-modal'].show()
      this.$nextTick().then(() => {
        this.$_getPlace()
        this.isReportAvailable && this.$_getReportCount()
      })
    },

    /**
     * プレイス情報を取得する
     */
    $_getPlace: async function () {
      if (!this.placeId) {
        return
      }

      this.$_showLoading()
      // URL作成
      const url = RestApiClient.createApiUrl(`place/${this.placeId}`)
      try {
        const d = await RestApiClient.executeGetApi(url).then(
          ({ data }) => data
        )
        this.data = {
          ...d,
          fix_at: toLocalTz(d.fix_at),
          create_at: toLocalTz(d.create_at),
          update_at: toLocalTz(d.update_at),
          place_attributes: d.place_attributes.map((attr) =>
            toLocalTzAttribute(attr)
          ),
        }
      } catch (error) {
        this.handleApiError(error)
        return
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * プレイスに対する報告書の件数を取得する
     */
    $_getReportCount: async function () {
      const url = RestApiClient.createApiUrl('report/count')
      const params = url.searchParams
      params.append('place_id', +this.placeId)
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.reportCount = json.data.count
      } catch (error) {
        this.handleApiError(error)
      }
    },

    /**
     * モーダルダイアログを非表示にする
     */
    hideModal: function () {
      this.$refs['the-place-detail-modal'].hide()
    },

    /**
     * モーダルダイアログを表示する時の処理
     */
    $_onShowModal: function () {
      // 地図を作り直さないよう、一度生成した中身は閉じても保持する
      this.isContentCreated = true
      this.isModalShown = true
    },

    /**
     * モーダルダイアログを非表示にする時の処理
     */
    $_onHideModal: function () {
      this.isModalShown = false
      // サニタイズ
      this.data = null
      this.placeId = null
      // イベントを発生させる
      this.$emit('hide-modal', this.isUpdated)
    },

    /**
     * 位置を確定する
     */
    $_fixPosition: async function () {
      const url = RestApiClient.createApiUrl(
        `place/position_fix/${this.placeId}`
      )
      const body = { position_fix_flg: true }

      try {
        await RestApiClient.executePutApi(url, body)
      } catch (error) {
        this.handleApiError(error)
        return
      }

      this.isUpdated = true
      // プレイス情報を再取得する
      this.$_getPlace()
    },

    /**
     * ビューのbackボタンクリック時処理
     */
    $_onBackClick() {
      this.viewIndex = Math.max(this.viewIndex - 1, 0)
    },

    /**
     * ビューのnextボタンクリック時処理
     */
    $_onNextClick() {
      this.viewIndex = Math.min(this.viewIndex + 1, this.viewCount - 1)
    },

    /**
     * 編集用モーダルを表示
     */
    editStart: function () {
      this.$refs['place-editor'].showModal(this.placeId)
    },

    /**
     * 編集用モーダル終了時の処理
     */
    editEnd: function (id) {
      this.isUpdated = true
      this.placeId = id
      this.$_getPlace()
    },

    /**
     * 近くのユーザー検索ボタンクリック時の処理
     */
    $_onSearchUserClick: function () {
      /**
       * プレイスに対する近くのユーザー検索ボタンクリックイベント
       * @param {Object} params 検索パラメータ
       */
      this.$emit('place-searchuser-click', {
        placeId: this.placeId,
        indoorLevelId: this.data.indoor_level_id,
      })
      this.$bvModal.hide('the-place-detail')
    },

    /**
     * 報告書検索ボタンクリック時の処理
     */
    $_onSearchReportClick: function () {
      /**
       * プレイスに対する報告書検索ボタンクリックイベント
       * @param {Array.<Number>} placeIds 対象プレイスID配列
       */
      this.$emit('place-searchreport-click', [this.placeId])
      this.$bvModal.hide('the-place-detail')
    },

    /**
     * メッセージ送信ダイアログを表示する
     */
    $_showMessagePost: function () {
      this.$refs['message-post-dialog'].showModal()
    },

    /**
     * スケジュール作成ダイアログを表示する
     */
    $_showScheduleEditor: function () {
      this.$refs['schedule-editor'].showModal(null, {
        place: {
          id: this.placeId,
          disabled: true,
        },
      })
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .place-detail-dialog-body {
  overflow: hidden;
}

.content {
  background-color: #ffffff;
  height: 600px;
  width: 100%;
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr) max-content;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px 22px;
  margin-bottom: 16px;
  background-color: $dialog-background-color;

  &.no-kana {
    padding: 14px 22px;

    .place-name {
      padding-top: 0;
    }
  }

  .place-name {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    row-gap: 4px;
    padding-top: 4px;

    .name {
      color: #2c3e50;
      font-size: $font-size-large;
      font-weight: $font-weight-bold;
      margin: 0;
      line-height: 1;
    }

    .kana {
      color: #989898;
      font-size: $font-size-small;
      margin: 0;
      line-height: 1;
    }
  }
  .action-text {
    display: flex;
    justify-content: center;
    align-items: center;
    column-gap: 16px;
    font-size: $font-size-middle;
    line-height: 1;
    .clickable {
      margin-bottom: 0;
      color: $text-color-link;
      text-decoration: underline;
      cursor: pointer;
    }
    .normal {
      margin-bottom: 0;
      pointer-events: none;
    }
  }
}
.panel-container {
  --index: 0;
  $-border: 1px solid #f0f0f0;

  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
  border-top: $-border;
  border-bottom: $-border;

  &.first-view {
    border-left: $-border;
  }
  &.last-view {
    border-right: $-border;
  }

  $-panel-width: calc((100% - 16px) / 2);

  .panels {
    width: auto;
    height: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    column-gap: 16px;
    padding: 0 16px;

    position: relative;
    left: calc(-1 * var(--index) * #{$-panel-width});
    transition: left ease 0.5s;

    ::v-deep .panel {
      width: $-panel-width;
      flex-shrink: 0;
    }
  }
}

.move-button {
  width: 32px;
  height: 80px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  opacity: 0.5;
  position: absolute;
  top: calc(50% - 40px);
  transition: opacity ease 0.3s;

  &:hover {
    opacity: 1;
    cursor: pointer;
  }

  &:focus,
  &:active {
    box-shadow: none !important;
  }

  &.back {
    left: 2px;
  }

  &.next {
    right: 2px;
  }
}

.arrow {
  fill: none;
  stroke: $entity-color-white;
  width: 48px;
  height: 48px;
  margin: -10px;
  stroke-width: 1.5px;
  stroke-linecap: round;
  stroke-linejoin: round;
  position: relative;

  .back & {
    left: -1px;
    -webkit-transform: rotate(90deg);
    -moz-transform: rotate(90deg);
    -ms-transform: rotate(90deg);
    -o-transform: rotate(90deg);
    transform: rotate(90deg);
  }

  .next & {
    right: -1px;
    -webkit-transform: rotate(-90deg);
    -moz-transform: rotate(-90deg);
    -ms-transform: rotate(-90deg);
    -o-transform: rotate(-90deg);
    transform: rotate(-90deg);
  }
}

.view-indicator {
  width: 100%;
  height: auto;
  margin-top: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 24px;

  .indicator-item {
    width: 8px;
    height: 8px;
    border: 1px solid #707070;
    border-radius: 50%;
    cursor: pointer;

    &.active {
      background-color: #707070;
      cursor: auto;
    }
  }
}
</style>
